// Stock arithmetic (FR-INV-*, FR-DSP-3). Stock on hand is never stored: it is the
// sum of append-only movements per batch, so the same number is derived on every
// device and can be audited back to the receipt or dispense that caused it.
import type { AbstractPowerSyncDatabase } from '@powersync/web';

export interface BatchStock {
  batch_id: string;
  drug_id: string;
  batch_no: string;
  expiry_on: string | null;
  unit_cost: number | null;
  qty_on_hand: number;
}

export interface Allocation { batch_id: string; qty: number; expiry_on: string | null; batch_no: string }

export interface AllocationResult {
  allocations: Allocation[];
  allocated: number;
  shortfall: number;          // how much could not be covered
  usedExpiring: boolean;      // something within 30 days of expiry was taken
}

/** Days until expiry, negative when already expired. */
export function daysToExpiry(expiry: string | null, today = new Date()): number | null {
  if (!expiry) return null;
  const e = new Date(expiry + 'T00:00:00');
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((e.getTime() - t.getTime()) / 86_400_000);
}

export function isExpired(expiry: string | null, today = new Date()): boolean {
  const d = daysToExpiry(expiry, today);
  return d !== null && d < 0;
}

/**
 * First-expiry-first-out allocation.
 * Expired batches are never taken automatically: dispensing an expired medicine
 * is a patient-safety event, not a rounding detail. Batches with no expiry date
 * are used last, because a known date is better evidence than none.
 */
export function allocateFefo(batches: BatchStock[], needed: number, today = new Date()): AllocationResult {
  const usable = batches
    .filter((b) => b.qty_on_hand > 0 && !isExpired(b.expiry_on, today))
    .sort((a, b) => {
      if (a.expiry_on && b.expiry_on) return a.expiry_on.localeCompare(b.expiry_on);
      if (a.expiry_on) return -1;   // dated batches before undated ones
      if (b.expiry_on) return 1;
      return a.batch_no.localeCompare(b.batch_no);
    });

  const allocations: Allocation[] = [];
  let remaining = needed;
  let usedExpiring = false;
  for (const b of usable) {
    if (remaining <= 0) break;
    const take = Math.min(remaining, b.qty_on_hand);
    if (take <= 0) continue;
    allocations.push({ batch_id: b.batch_id, qty: take, expiry_on: b.expiry_on, batch_no: b.batch_no });
    const d = daysToExpiry(b.expiry_on, today);
    if (d !== null && d <= 30) usedExpiring = true;
    remaining -= take;
  }
  return { allocations, allocated: needed - remaining, shortfall: Math.max(remaining, 0), usedExpiring };
}

/** Batch-level stock for one drug. */
export async function batchesForDrug(db: AbstractPowerSyncDatabase, drugId: string): Promise<BatchStock[]> {
  return db.getAll<BatchStock>(
    `select b.id as batch_id, b.drug_id, b.batch_no, b.expiry_on, b.unit_cost,
            coalesce((select sum(m.qty) from stock_movements m where m.batch_id = b.id), 0) as qty_on_hand
     from drug_batches b where b.drug_id = ? order by b.expiry_on`, [drugId]);
}

export interface DrugStock {
  drug_id: string; generic_name: string; brand_name: string | null; strength: string | null; form: string | null;
  dispense_unit: string; selling_price: number; reorder_level: number; is_consumable: number;
  qty_on_hand: number; nearest_expiry: string | null; batch_count: number;
}

/** Stock across the whole clinic, optionally filtered. Expired quantities are excluded from qty_on_hand. */
export async function stockList(db: AbstractPowerSyncDatabase, clinicId: string, opts: { search?: string; lowOnly?: boolean } = {}): Promise<DrugStock[]> {
  const like = `%${(opts.search ?? '').trim().toLowerCase()}%`;
  const where = opts.search?.trim() ? 'and (lower(d.generic_name) like ? or lower(d.brand_name) like ?)' : '';
  const params: unknown[] = opts.search?.trim() ? [clinicId, like, like] : [clinicId];
  const rows = await db.getAll<DrugStock>(
    `select d.id as drug_id, d.generic_name, d.brand_name, d.strength, d.form, d.dispense_unit, d.selling_price,
            d.reorder_level, d.is_consumable,
            coalesce((select sum(m.qty) from stock_movements m join drug_batches b on b.id = m.batch_id
                      where b.drug_id = d.id and (b.expiry_on is null or b.expiry_on >= date('now','localtime'))), 0) as qty_on_hand,
            (select min(b.expiry_on) from drug_batches b
             where b.drug_id = d.id and b.expiry_on is not null
               and coalesce((select sum(m.qty) from stock_movements m where m.batch_id = b.id), 0) > 0) as nearest_expiry,
            (select count(*) from drug_batches b where b.drug_id = d.id) as batch_count
     from drugs d where d.clinic_id = ? and d.active = 1 ${where} order by d.generic_name limit 400`, params);
  return opts.lowOnly ? rows.filter((r) => r.qty_on_hand <= r.reorder_level) : rows;
}

/** Batches expiring within the window, or already expired, that still hold stock. */
export async function expiringBatches(db: AbstractPowerSyncDatabase, clinicId: string, withinDays = 90) {
  return db.getAll<BatchStock & { generic_name: string; brand_name: string | null; strength: string | null }>(
    `select b.id as batch_id, b.drug_id, b.batch_no, b.expiry_on, b.unit_cost,
            coalesce((select sum(m.qty) from stock_movements m where m.batch_id = b.id), 0) as qty_on_hand,
            d.generic_name, d.brand_name, d.strength
     from drug_batches b join drugs d on d.id = b.drug_id
     where b.clinic_id = ? and b.expiry_on is not null
       and b.expiry_on <= date('now','localtime','+' || ? || ' days')
       and coalesce((select sum(m.qty) from stock_movements m where m.batch_id = b.id), 0) > 0
     order by b.expiry_on`, [clinicId, withinDays]);
}

/** Total value of stock at cost, for the owner's report. */
export async function stockValue(db: AbstractPowerSyncDatabase, clinicId: string): Promise<number> {
  const r = await db.getOptional<{ v: number | null }>(
    `select sum(coalesce(b.unit_cost, 0) * coalesce((select sum(m.qty) from stock_movements m where m.batch_id = b.id), 0)) as v
     from drug_batches b where b.clinic_id = ?`, [clinicId]);
  return Math.round(r?.v ?? 0);
}
